import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useRepoMarginCallBalance } from '@/hooks';
import {
  bookKey,
  isHistoryItemNotFound,
  parseBookKey,
} from '@/hooks/trades/use-history-pages';
import { useMtmRun, useStockLbRunBooks, useStockLbRunPages } from '@/hooks/trades/use-mtm-runs';
import {
  repoBookKey,
  useRepoMarginCall,
  useRepoMarginCallBooks,
  useRepoMarginCallPages,
} from '@/hooks/trades/use-repo-margin-call-history';
import type { HistoryFeedFilters, MtmRunItem, RepoMarginCall } from '@/types';
import { parsePositiveIntParam } from '@/utils/search-params';
import { RepoCashPoolLedger } from '../mtm/RepoCashPoolLedger';
import { CounterpartyRail } from './CounterpartyRail';
import { type HistoryView } from './HistoryViewToggle';
import { DateGroupedList, type DateEventRow, type DateGroup } from './DateGroupedList';
import { LoadMore } from './LoadMore';
import { CounterpartyList, type CpListRow, type CpGroup } from './CounterpartyList';
import {
  DetailHeader,
  type DetailChip,
  type DetailChartSection,
} from './DetailHeader';
import { RunLineItems } from './RunLineItems';
import { RunLedger } from './RunLedger';
import { MarginChart, type MarginChartDatum } from './MarginChart';
import {
  dayFmt,
  dayShortFmt,
  timeFmt,
  timeTitle,
  byLabel,
  belowMtaBadge,
  fxPairLabel,
  poolRailTag,
  signedNumber,
  moneyAbbrev,
  moneyFull,
  belowMta,
  relativeDayLabel,
  dayDiff,
  netDirectionCaption,
  type ChartRange,
  EXPOSURE_ACCENT,
} from './history-utils';
import {
  displayCashPool,
  cashPoolHolderCaption,
  cashPoolRailCaption,
  bookBalanceCurrency,
  differsFromLastCall,
  LAST_CALL_MISMATCH,
} from '@/utils/trades/cash-pool-display';
import { formatMta, resolveMtaCurrency } from '@/utils/trades/mta-display';

// LR-498: the unified Trades → History master–detail shell. One two-pane container for both the
// Mark to Market and Cash Pool sub-tabs: a left rail that toggles between "By date" (a chronological
// blotter across all counterparties) and "By counterparty" (one CP's history), and a right detail
// pane that adapts so the date axis lives in exactly one place. Replaces the old day→CP→run /
// CP×currency accordions (MtmRunHistory + CashPoolPostings).

// ─── shared layout + states ──────────────────────────────────────────────────

const PANE = 'flex min-h-0 flex-1';

// LR-577 (feedback #69): every trade opened from this shell returns to History.
const TRADE_ORIGIN = { from: 'history' } as const;

/** The paging state a list hands to its "load more" control. */
interface Paging {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  fetchNextPage: () => unknown;
}

const HistoryLayout: React.FC<{
  body: BodyProps;
  resultCount?: number;
  railPaging: Paging;
  loadMoreLabel: string;
  railList: React.ReactNode;
  detail: React.ReactNode;
}> = ({ body, resultCount, railPaging, loadMoreLabel, railList, detail }) => (
  <div className={PANE}>
    <CounterpartyRail
      hview={body.hview}
      onHviewChange={body.setHview}
      query={body.query}
      onQueryChange={body.setQuery}
      resultCount={resultCount}
      hasMore={railPaging.hasNextPage}
      isLoadingMore={railPaging.isFetchingNextPage}
      loadMoreFailed={railPaging.isFetchNextPageError}
      onLoadMore={() => void railPaging.fetchNextPage()}
      loadMoreLabel={loadMoreLabel}
    >
      {railList}
    </CounterpartyRail>
    <div className="min-w-0 flex-1 overflow-y-auto">{detail}</div>
  </div>
);

const SkeletonBar: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`animate-pulse rounded bg-lr-bg ${className ?? ''}`} />
);

const DetailSkeleton: React.FC = () => (
  <div className="min-w-0 flex-1 p-6" data-testid="history-detail-skeleton">
    <SkeletonBar className="h-[15px] w-2/5" />
    <SkeletonBar className="mt-2.5 h-[26px] w-3/5" />
    <SkeletonBar className="mt-3.5 h-16 w-full" />
    <div className="mt-4 flex flex-col gap-2.5">
      {[100, 92, 96, 88].map((w, i) => (
        <SkeletonBar key={i} className="h-2.5" />
      ))}
    </div>
  </div>
);

const HistorySkeleton: React.FC = () => (
  <div className={PANE}>
    <div className="flex w-[374px] shrink-0 flex-col gap-4 border-r border-lr-border p-[18px]">
      {[78, 64, 70, 60].map((w, i) => (
        <div key={i} className="flex flex-col gap-1.5" style={{ width: `${w}%` }}>
          <SkeletonBar className="h-[11px]" />
          <SkeletonBar className="h-2 w-2/3" />
        </div>
      ))}
    </div>
    <DetailSkeleton />
  </div>
);

// The search matched nothing: the rail says so, and the detail has nothing to show.
const NoMatchDetail: React.FC<{ noun: string }> = ({ noun }) => (
  <p className="p-6 text-sm text-lr-faint">No {noun} match the search.</p>
);

const HistoryError: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => (
  <div className="flex flex-1 items-start gap-3 p-6">
    <p className="text-sm text-red-600" role="alert">
      {message}
    </p>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="rounded-[7px] border border-lr-border px-3 py-1 text-xs font-semibold text-lr-fg-2 hover:bg-lr-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent"
      >
        Retry
      </button>
    )}
  </div>
);

const HistoryEmpty: React.FC<{ tab: 'mtm' | 'cashpool'; onGoToMtm?: () => void }> = ({
  tab,
  onGoToMtm,
}) => (
  <div className="flex flex-1 flex-col items-center justify-center px-9 py-[60px] text-center">
    <div
      aria-hidden
      className="flex h-[46px] w-[46px] items-center justify-center rounded-[11px] border-[1.5px] border-dashed border-lr-border-2 text-[22px] text-lr-faint"
    >
      —
    </div>
    {tab === 'mtm' ? (
      <>
        <div className="mt-4 text-[15px] font-semibold text-lr-fg-2">No mark-to-market runs yet</div>
        <div className="mt-[7px] max-w-[240px] text-[13px] leading-relaxed text-lr-faint">
          Runs appear here after the first Stock&nbsp;L/B mark-to-market for a counterparty.
        </div>
        {onGoToMtm && (
          <button
            type="button"
            onClick={onGoToMtm}
            className="mt-[18px] rounded-[7px] border border-lr-accent-line bg-lr-accent-weak px-4 py-2 text-[13px] font-semibold text-lr-accent hover:bg-lr-accent-line/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent"
          >
            Go to Mark to Market →
          </button>
        )}
      </>
    ) : (
      <>
        <div className="mt-4 text-[15px] font-semibold text-lr-fg-2">No cash-pool postings yet</div>
        <div className="mt-[7px] max-w-[260px] text-[13px] leading-relaxed text-lr-faint">
          Postings appear here after the first repo margin call is posted for a counterparty.
        </div>
      </>
    )}
  </div>
);

const SectionBar: React.FC<{ title: string; meta: string }> = ({ title, meta }) => (
  <div className="flex items-center justify-between px-6 pb-2 pt-3">
    <span className="text-[13px] font-bold text-lr-fg">{title}</span>
    <span className="whitespace-nowrap text-[11.5px] text-lr-faint">{meta}</span>
  </div>
);

// A deep link (`?sel=`) naming an id the server does not hold (or not in this tab) falls back to
// the newest row; say so, or the detail reads as the linked item. `sel` is echoed only as a valid
// id, so a shared link cannot put its own text into this notice. `failed` = the lookup itself
// failed (not a 404), so the item may well exist.
const SelectionNotFoundBanner: React.FC<{ noun: string; sel: string; failed?: boolean }> = ({
  noun,
  sel,
  failed,
}) => {
  const item = noun.toLowerCase();
  const head = parsePositiveIntParam(sel) === undefined ? `The linked ${item}` : `${noun} #${sel}`;
  return (
    <p className="border-b border-amber-200 bg-amber-50 px-6 py-2 text-xs text-amber-800">
      {head} {failed ? 'could not be loaded' : 'is not in the list'}; the most recent {item} is shown
      instead.
    </p>
  );
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function relDayWord(d: Date, now: Date): string {
  const diff = dayDiff(now, d);
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  return dayShortFmt.format(d);
}

/** The signed net-due of a posting, sign re-derived from `direction` (defensive — net_due is
 *  canonically signed, and since feedback #62 the cash-pool ledger renders it signed as-is). */
function signedNetDue(call: RepoMarginCall): number {
  if (call.direction === 'none') return 0;
  const mag = Math.abs(Number(call.net_due));
  return call.direction === 'due_to_cp' ? -mag : mag;
}

// LR-493: a cross-currency book settles in `settlement_currency` and the transfer (net due) may be
// PAID in a different major (`payment_currency`) at `payment_fx_rate`. The running balance / pool is
// denominated in the settlement currency; the net-due transfer is shown in the pay-in currency. A
// same-currency book has settlement_currency === currency, no payment_currency and rate 1, so all
// three collapse to the trade currency (back-compat).
function settleCcyOf(call: RepoMarginCall): string {
  return call.settlement_currency || call.currency;
}
function payCcyOf(call: RepoMarginCall): string {
  return call.payment_currency || settleCcyOf(call);
}
function payRateOf(call: RepoMarginCall): number {
  return call.payment_fx_rate ? Number(call.payment_fx_rate) : 1;
}

// LR-493: chip value for a cross-ccy leg — the result currency prominent, the FX pair/rate as
// smaller muted inline subtext on the same line (e.g. "EUR  GBP→EUR 1.18").
function ccyRateValue(ccy: string, rate: React.ReactNode): React.ReactNode {
  return (
    <>
      {ccy} <span className="ml-0.5 text-[11px] font-normal text-lr-faint">{rate}</span>
    </>
  );
}

// LR-493: detail-header chips flagging a cross-currency call/run. Empty on a same-currency book/run
// (settlement == trade, no pay-in, rate 1), so the common case is unchanged.
function repoFxChips(call: RepoMarginCall): DetailChip[] {
  const settle = settleCcyOf(call);
  const chips: DetailChip[] = [];
  // Two DISTINCT legs of the chain trade → settlement → payment; label them as different steps
  // ("Settled in" vs "Paid in", format parity with the MTM "Run in" chip) so they don't read as two
  // FX cards. The figures (pool / totals) are DENOMINATED in `settle` at the COB trade→settlement rate.
  if (settle.toUpperCase() !== call.currency.toUpperCase()) {
    chips.push({
      label: 'Settled in',
      value: ccyRateValue(settle, fxPairLabel(call.currency, settle, call.cob_fx_rate)),
    });
  }
  // The transfer (net due) was actually PAID in a different major than the settlement currency, at
  // the settlement→payment rate.
  if (call.payment_currency && call.payment_currency.toUpperCase() !== settle.toUpperCase()) {
    chips.push({
      label: 'Paid in',
      value: ccyRateValue(
        call.payment_currency,
        fxPairLabel(settle, call.payment_currency, call.payment_fx_rate)
      ),
    });
  }
  return chips;
}
function mtmFxChip(run: MtmRunItem): DetailChip[] {
  // A target-denominated stock_lb run: net/deltas are in `currency` (the target), converted from the
  // native trade currency at `fx_rate`. A mark is a valuation, not a transfer, so there is no pay-in
  // (unlike a repo call) — the chip reads "Run in EUR · GBP→EUR 1.18" (denomination + FX pair).
  const from = run.trade_currency;
  const denominated = run.fx_rate != null && Number(run.fx_rate) !== 1;
  return denominated && from && from.toUpperCase() !== run.currency.toUpperCase()
    ? [{ label: 'Run in', value: ccyRateValue(run.currency, fxPairLabel(from, run.currency, run.fx_rate)) }]
    : [];
}

/** LR-498 v2 (T1-2): collapse the flat name|currency rows into per-counterparty groups (one header,
 *  its currency books beneath), preserving first-seen order. */
function groupByName(rows: CpListRow[]): CpGroup[] {
  const m = new Map<string, CpListRow[]>();
  for (const r of rows) {
    const arr = m.get(r.cp);
    if (arr) arr.push(r);
    else m.set(r.cp, [r]);
  }
  return [...m.entries()].map(([name, rs]) => ({ name, rows: rs }));
}

// Chart range windows (the 1M/3M/1Y selector). Clamp the chart series to the window ending now;
// if the window would be empty (sparse history), fall back to the full series so the chart is
// never blank.
const RANGE_DAYS: Record<ChartRange, number> = { '1M': 30, '3M': 90, '1Y': 365 };
const DAY_MS = 86_400_000;

function clampByRange<T>(items: T[], iso: (t: T) => string, range: ChartRange, now: Date): T[] {
  const cutoff = now.getTime() - RANGE_DAYS[range] * DAY_MS;
  const within = items.filter((t) => new Date(iso(t)).getTime() >= cutoff);
  return within.length ? within : items;
}

/** The counterparty's MTA as the run reports it (its current value), or undefined for none. */
function mtaOf(run: MtmRunItem): { mta: number; ccy?: string } | undefined {
  return run.counterparty_minimum_transfer_amount != null
    ? {
        mta: Number(run.counterparty_minimum_transfer_amount),
        ccy: run.counterparty_minimum_transfer_amount_currency,
      }
    : undefined;
}

/** Newest-first rows under one header per day; a day split across two pages merges back. */
function groupByDay<T>(
  items: T[],
  at: (item: T) => string,
  toRow: (item: T) => DateEventRow,
  now: Date
): DateGroup[] {
  const dayMap = new Map<string, T[]>();
  for (const item of items) {
    const key = dayFmt.format(new Date(at(item)));
    const arr = dayMap.get(key);
    if (arr) arr.push(item);
    else dayMap.set(key, [item]);
  }
  return [...dayMap.values()].map((day) => ({
    label: relativeDayLabel(new Date(at(day[0])), now),
    rows: day.map(toRow),
  }));
}

/** The id a By-date `?sel=` asks to fetch on its own: only when the loaded pages do not hold it. */
function linkedIdToFetch(
  items: { id: number }[],
  sel: string | undefined,
  searching: boolean,
  feedSettling: boolean
): number | undefined {
  if (sel === undefined || searching || feedSettling) return undefined;
  if (items.some((r) => String(r.id) === sel)) return undefined;
  return parsePositiveIntParam(sel);
}

interface LinkedSelection<T> {
  /** undefined = nothing to show yet (the linked item is still loading). */
  row: T | undefined;
  missing: boolean;
  failed: boolean;
}

/**
 * The By-date row `?sel=` names. A row on the loaded pages is used as-is; an older one is fetched
 * by id (`linked`), and only a 404 (or a row of another bucket) flags it as missing. Under a
 * search, a linked row the search hides is not "missing": the first match is shown.
 */
function resolveLinked<T extends { id: number }>({
  items,
  sel,
  searching,
  feedSettling,
  linked,
  accept = () => true,
}: {
  items: T[];
  sel: string | undefined;
  searching: boolean;
  /** The rail still shows the previous search's rows. */
  feedSettling: boolean;
  linked: { data?: T; isLoading: boolean; error: unknown };
  accept?: (row: T) => boolean;
}): LinkedSelection<T> {
  const inFeed = sel === undefined ? undefined : items.find((r) => String(r.id) === sel);
  if (inFeed) return { row: inFeed, missing: false, failed: false };
  if (sel === undefined || searching) return { row: items[0], missing: false, failed: false };
  if (linked.data && accept(linked.data)) return { row: linked.data, missing: false, failed: false };
  if (feedSettling || linked.isLoading) return { row: undefined, missing: false, failed: false };
  const failed = linked.error != null && !isHistoryItemNotFound(linked.error);
  return { row: items[0], missing: true, failed };
}

/** Keeps loading a book's older pages until they reach back past the chart's range. A failed page
 *  stops it: retrying is the "load older" button's job, or this would refetch in a loop. */
function useRangeCovered(oldestAt: string | undefined, paging: Paging, range: ChartRange): void {
  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = paging;
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError || oldestAt === undefined) return;
    if (new Date(oldestAt).getTime() >= Date.now() - RANGE_DAYS[range] * DAY_MS) {
      void fetchNextPage();
    }
  }, [oldestAt, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage, range]);
}

/**
 * The By-counterparty book `?sel=` names, or the rail's first book. A key the server holds no rows
 * for (stale or mistyped) gives way to the first book once its fetch comes back empty. Under a
 * search the rail and the detail agree: a book the search hides gives way to the first match.
 */
function useSelectedBook<B>(
  books: B[],
  sel: string | undefined,
  searching: boolean,
  keyOf: (book: B) => string,
  filtersOf: (key: string | undefined) => HistoryFeedFilters | undefined
) {
  const [deadSel, setDeadSel] = useState<string>();
  const firstKey = books[0] ? keyOf(books[0]) : undefined;
  const listed = (key: string | undefined) =>
    key === undefined ? undefined : books.find((b) => keyOf(b) === key);
  const usable =
    sel !== undefined &&
    sel !== deadSel &&
    filtersOf(sel) !== undefined &&
    (!searching || listed(sel) !== undefined);
  const key = usable ? sel : firstKey;
  const rejectIfEmpty = useCallback(
    (empty: boolean) => {
      if (empty && key === sel && sel !== firstKey) setDeadSel(sel);
    },
    [key, sel, firstKey]
  );
  return { key, filters: filtersOf(key), listed: listed(key), rejectIfEmpty };
}

// ─── Mark to Market ──────────────────────────────────────────────────────────

interface BodyProps {
  hview: HistoryView;
  sel?: string;
  query: string;
  setQuery: (q: string) => void;
  setHview: (v: HistoryView) => void;
  setSel: (s: string) => void;
  goToMtm: () => void;
}

const isStockLb = (run: MtmRunItem): boolean => run.bucket === 'stock_lb';

const mtaChip = (run: MtmRunItem): DetailChip[] => {
  const mta = mtaOf(run);
  return mta ? [{ label: 'Min transfer (MTA)', value: formatMta(mta.mta, mta.ccy) }] : [];
};

const MtmByDate: React.FC<BodyProps> = (body) => {
  const { sel, query, setSel, goToMtm } = body;
  const searching = query !== '';
  const feed = useStockLbRunPages({ search: query || undefined }, { keepPrevious: true });
  const runs = feed.items;
  const linked = useMtmRun(
    linkedIdToFetch(runs, sel, searching, feed.isPending || feed.isPlaceholderData)
  );

  if (feed.isPending) return <HistorySkeleton />;
  if (feed.error && !feed.data) {
    return <HistoryError message="Failed to load MTM runs." onRetry={() => void feed.refetch()} />;
  }
  if (!searching && !feed.isPlaceholderData && runs.length === 0) {
    return <HistoryEmpty tab="mtm" onGoToMtm={goToMtm} />;
  }

  const now = new Date();
  const groups = groupByDay(
    runs,
    (r) => r.marked_at,
    (r) => {
      const net = Number(r.net);
      return {
        id: String(r.id),
        time: timeFmt.format(new Date(r.marked_at)),
        timeTitle: timeTitle(new Date(r.marked_at)),
        cp: r.counterparty_name,
        net,
        ccy: r.currency,
        belowMta: belowMta(net, mtaOf(r), r.currency),
      };
    },
    now
  );
  const { row: selectedRun, missing, failed } = resolveLinked({
    items: runs,
    sel,
    searching,
    feedSettling: feed.isPlaceholderData,
    linked,
    // The id may name a repo run, which this tab does not show.
    accept: isStockLb,
  });

  let detail: React.ReactNode;
  if (!selectedRun) {
    detail = runs.length === 0 ? <NoMatchDetail noun="runs" /> : <DetailSkeleton />;
  } else {
    const at = new Date(selectedRun.marked_at);
    const selNet = Number(selectedRun.net);
    const selMta = mtaOf(selectedRun);
    const selBelow = belowMta(selNet, selMta, selectedRun.currency);
    // LR-577 (feedback #68): the label follows the sign the hero figure already carries — a run in
    // our favour used to be captioned "due to CP".
    const selDir = netDirectionCaption(selNet);
    detail = (
      <>
        {missing && sel !== undefined && (
          <SelectionNotFoundBanner noun="Run" sel={sel} failed={failed} />
        )}
        <DetailHeader
          title={selectedRun.counterparty_name}
          pill={{ tone: 'accent', label: 'Stock L/B' }}
          heroLabel={selDir ? `Selected run · ${selDir}` : 'Selected run'}
          heroValue={selNet}
          heroValueText={`${moneyFull(selNet)} ${selectedRun.currency}`}
          heroTone={selBelow ? 'muted' : undefined}
          heroBadge={selBelow && selMta ? belowMtaBadge(selMta) : undefined}
          heroMeta={`${selectedRun.currency} · ${dayFmt.format(at)} · ${timeFmt.format(at)} UTC · ${byLabel(
            selectedRun.marked_by,
            selectedRun.source
          )}${selBelow && selMta ? ` · MTA ${formatMta(selMta.mta, selMta.ccy)}` : ''}`}
          chips={[
            { label: 'Marks', value: String(selectedRun.mark_count) },
            ...mtmFxChip(selectedRun),
            ...mtaChip(selectedRun),
          ]}
        />
        <SectionBar
          title="Marked instruments"
          meta={`${dayFmt.format(at)} · ${timeFmt.format(at)} UTC run · ${selectedRun.mark_count} marks`}
        />
        <RunLineItems
          runId={selectedRun.id}
          currency={selectedRun.currency}
          fxRate={selectedRun.fx_rate}
          net={selectedRun.net}
        />
      </>
    );
  }
  return (
    <HistoryLayout
      body={body}
      resultCount={searching && !feed.isPlaceholderData ? feed.totalCount : undefined}
      railPaging={feed}
      loadMoreLabel="Load more runs"
      railList={
        <DateGroupedList
          groups={groups}
          selectedId={selectedRun ? String(selectedRun.id) : undefined}
          onSelect={setSel}
          emptyLabel="No matching runs."
        />
      }
      detail={detail}
    />
  );
};

const MtmByCounterparty: React.FC<BodyProps> = (body) => {
  const { sel, query, setSel, goToMtm } = body;
  const searching = query !== '';
  const books = useStockLbRunBooks({ search: query || undefined });
  const selected = useSelectedBook(books.items, sel, searching, bookKey, parseBookKey);
  const runs = useStockLbRunPages(selected.filters ?? {}, { enabled: selected.filters !== undefined });
  const [range, setRange] = useState<ChartRange>('3M');
  const { rejectIfEmpty } = selected;
  const runsSettledEmpty = runs.isSuccess && runs.items.length === 0;
  useEffect(() => rejectIfEmpty(runsSettledEmpty), [rejectIfEmpty, runsSettledEmpty]);
  useRangeCovered(runs.items[runs.items.length - 1]?.marked_at, runs, range);

  if (books.isPending) return <HistorySkeleton />;
  if (books.error && !books.data) {
    return <HistoryError message="Failed to load MTM runs." onRetry={() => void books.refetch()} />;
  }
  if (!searching && !books.isPlaceholderData && books.items.length === 0) {
    return <HistoryEmpty tab="mtm" onGoToMtm={goToMtm} />;
  }

  const now = new Date();
  // Currency is a per-book tag (T1-1) and the "Stock L/B" bucket is the group-header tag (T1-2), so
  // the meta carries only the run count + last date.
  const rows: CpListRow[] = books.items.map((b) => {
    const net = Number(b.latest.net);
    return {
      key: bookKey(b),
      cp: b.counterparty_name,
      currency: b.currency,
      meta: `${b.run_count} ${b.run_count === 1 ? 'run' : 'runs'} · last ${dayShortFmt.format(
        new Date(b.latest.marked_at)
      )}`,
      value: net,
      valueCaption: 'latest run',
      belowMta: belowMta(net, mtaOf(b.latest), b.latest.currency),
    };
  });

  const latest = runs.items[0] ?? selected.listed?.latest;
  let detail: React.ReactNode;
  if (!latest) {
    detail = runs.error ? (
      <HistoryError
        message="Failed to load this counterparty's runs."
        onRetry={() => void runs.refetch()}
      />
    ) : books.items.length === 0 ? (
      <NoMatchDetail noun="counterparties" />
    ) : (
      <DetailSkeleton />
    );
  } else {
    const latestNet = Number(latest.net);
    const latestMta = mtaOf(latest);
    const latestBelow = belowMta(latestNet, latestMta, latest.currency);
    const latestDir = netDirectionCaption(latestNet);
    const runCount = runs.totalCount ?? selected.listed?.run_count;
    // The ±MTA band is only meaningful when the threshold is in the chart's currency.
    const bandMta =
      latestMta && resolveMtaCurrency(latestMta.ccy) === latest.currency.toUpperCase()
        ? latestMta.mta
        : undefined;
    const chartData: MarginChartDatum[] = [...clampByRange(runs.items, (r) => r.marked_at, range, now)]
      .reverse()
      .map((r) => ({
        date: dayShortFmt.format(new Date(r.marked_at)),
        value: Number(r.net),
        label: signedNumber(Number(r.net)),
      }));
    detail = (
      <>
        <DetailHeader
          title={latest.counterparty_name}
          pill={{ tone: 'accent', label: 'Stock L/B' }}
          heroLabel={latestDir ? `Latest run · ${latestDir}` : 'Latest run'}
          heroValue={latestNet}
          heroValueText={`${moneyFull(latestNet)} ${latest.currency}`}
          heroTone={latestBelow ? 'muted' : undefined}
          heroBadge={latestBelow && latestMta ? belowMtaBadge(latestMta) : undefined}
          heroMeta={`${latest.currency} · ${dayFmt.format(new Date(latest.marked_at))} · ${byLabel(
            latest.marked_by,
            latest.source
          )}${latestBelow && latestMta ? ` · MTA ${formatMta(latestMta.mta, latestMta.ccy)}` : ''}`}
          chips={[
            ...(runCount !== undefined ? [{ label: 'Runs', value: String(runCount) }] : []),
            ...mtmFxChip(latest),
            ...mtaChip(latest),
          ]}
          chart={{
            title: 'Net per run',
            subtitle: 'per run · not a running level',
            range,
            onRangeChange: setRange,
            node: (
              <MarginChart
                key={selected.key}
                data={chartData}
                accent={EXPOSURE_ACCENT}
                variant="bars"
                unit="money"
                highlightIndex={chartData.length - 1}
                mtaBand={bandMta}
              />
            ),
          }}
        />
        <SectionBar title="Run history" meta="one run per day · Stock L/B" />
        {/* The header can stand on the rail's book row while the book's own runs load; the
            ledger waits for them (it opens on the newest), and their failure is said. */}
        {runs.data !== undefined ? (
          <RunLedger key={selected.key} runs={runs.items} />
        ) : (
          runs.error && (
            <HistoryError
              message="Failed to load this counterparty's runs."
              onRetry={() => void runs.refetch()}
            />
          )
        )}
        <LoadMore
          hasMore={runs.hasNextPage}
          isLoading={runs.isFetchingNextPage || (runs.isPending && runs.fetchStatus === 'fetching')}
          isError={runs.isFetchNextPageError}
          onLoadMore={() => void runs.fetchNextPage()}
          label="Load older runs"
        />
      </>
    );
  }
  return (
    <HistoryLayout
      body={body}
      resultCount={searching && !books.isPlaceholderData ? books.totalCount : undefined}
      railPaging={books}
      loadMoreLabel="Load more counterparties"
      railList={
        <CounterpartyList
          groups={groupByName(rows)}
          selectedKey={selected.key}
          onSelect={setSel}
          headerTag={{ label: 'Stock L/B', tone: 'accent' }}
          emptyLabel="No matching counterparties."
        />
      }
      detail={detail}
    />
  );
};

const MtmHistory: React.FC<BodyProps> = (body) =>
  body.hview === 'date' ? <MtmByDate {...body} /> : <MtmByCounterparty {...body} />;

// ─── Cash Pool ───────────────────────────────────────────────────────────────

// The hero is the book's balance from the pool ledger — what the next call nets against — and the
// breakdown splits it by the pools holding it (the book's share of each, so they add up to the hero).
// Everything else on the tab is the calls' frozen `cashpool_after` snapshot.
const RepoBookHeader: React.FC<{
  head: RepoMarginCall;
  chart: DetailChartSection;
}> = ({ head, chart }) => {
  const settleCcy = settleCcyOf(head);
  const book = useRepoMarginCallBalance(head.counterparty_name);

  const pools = book.data?.pools ?? [];
  // With no pool holding money the balance's own currency (the agreement's), not the last call's.
  const heroCcy = bookBalanceCurrency(pools, book.data?.currency || settleCcy);
  const stored = book.data?.cashpool;
  const balance =
    stored == null || heroCcy === null ? null : displayCashPool(Number(stored));
  // A failed background refetch keeps the cached figure, so only a read with no data is unavailable.
  const heroMeta =
    book.error && !book.data
      ? `${settleCcy} · ledger unavailable`
      : book.isLoading
        ? `${settleCcy} · loading pool ledger…`
        : heroCcy === null
          ? 'pool ledger · held in several currencies, no single total'
          : balance === null
            ? `${heroCcy} · pool ledger`
            : `${heroCcy} · pool ledger · ${cashPoolHolderCaption(balance)}`;

  const atLastCall = displayCashPool(Number(head.cashpool_after));
  const exposure = Number(head.total_exposure);
  const poolChips: DetailChip[] = pools.map((pool) => {
    const amount = moneyAbbrev(displayCashPool(Number(pool.amount)));
    return {
      label: pool.name,
      value: pool.currency === settleCcy ? amount : `${amount} ${pool.currency}`,
      tone: 'neutral',
    };
  });

  return (
    <DetailHeader
      title={head.counterparty_name}
      pill={{ tone: 'warn', label: 'Repo cash pool' }}
      heroLabel="Book balance"
      heroValue={balance}
      // LR-542 (variant A): the balance hero is NOT sign-coloured — a billing-signed
      // balance is a ledger figure, not an in-our-favour reading.
      heroColored={false}
      // LR-493: in the pool's currency (the settlement one), never the trade currency.
      heroValueText={
        balance === null ? undefined : `${moneyFull(balance)} ${heroCcy}`
      }
      heroMeta={heroMeta}
      heroBadge={
        balance !== null && differsFromLastCall(stored, head.cashpool_after)
          ? { tone: 'warn', ...LAST_CALL_MISMATCH }
          : undefined
      }
      chips={[
        { label: 'At last call', value: moneyAbbrev(atLastCall), tone: 'neutral' },
        {
          label: 'Open exposure',
          value: moneyAbbrev(exposure),
          tone: exposure < 0 ? 'neg' : exposure > 0 ? 'pos' : 'neutral',
        },
        ...repoFxChips(head),
      ]}
      breakdown={{ title: 'Held in pools', chips: poolChips }}
      chart={chart}
    />
  );
};

const CashPoolByDate: React.FC<BodyProps> = (body) => {
  const { sel, query, setSel } = body;
  const searching = query !== '';
  const feed = useRepoMarginCallPages({ search: query || undefined }, { keepPrevious: true });
  const calls = feed.items;
  const linked = useRepoMarginCall(
    linkedIdToFetch(calls, sel, searching, feed.isPending || feed.isPlaceholderData)
  );

  if (feed.isPending) return <HistorySkeleton />;
  if (feed.error && !feed.data) {
    return (
      <HistoryError message="Failed to load cash-pool postings." onRetry={() => void feed.refetch()} />
    );
  }
  if (!searching && !feed.isPlaceholderData && calls.length === 0) {
    return <HistoryEmpty tab="cashpool" />;
  }

  const now = new Date();
  const groups = groupByDay(
    calls,
    (c) => c.called_at,
    (c) => ({
      id: String(c.id),
      time: timeFmt.format(new Date(c.called_at)),
      timeTitle: timeTitle(new Date(c.called_at)),
      cp: c.counterparty_name,
      // LR-493: the net-due transfer is shown in the pay-in currency (settlement × payment rate).
      net: signedNetDue(c) * payRateOf(c),
      ccy: payCcyOf(c),
    }),
    now
  );
  const { row: selectedCall, missing, failed } = resolveLinked({
    items: calls,
    sel,
    searching,
    feedSettling: feed.isPlaceholderData,
    linked,
  });

  let detail: React.ReactNode;
  if (!selectedCall) {
    detail = calls.length === 0 ? <NoMatchDetail noun="postings" /> : <DetailSkeleton />;
  } else {
    const at = new Date(selectedCall.called_at);
    // LR-542 (feedback #63): the pool is displayed billing-signed (flipped vs storage).
    const poolAfter = displayCashPool(Number(selectedCall.cashpool_after));
    // LR-493: the net due is shown in the pay-in currency (settlement × payment rate); the pool
    // after (chip) stays in the settlement currency.
    const netCcy = payCcyOf(selectedCall);
    const netDue = signedNetDue(selectedCall) * payRateOf(selectedCall);
    detail = (
      <>
        {missing && sel !== undefined && (
          <SelectionNotFoundBanner noun="Margin call" sel={sel} failed={failed} />
        )}
        <DetailHeader
          title={selectedCall.counterparty_name}
          pill={{ tone: 'warn', label: 'Repo cash pool' }}
          heroLabel="Posting · net due"
          heroValue={netDue}
          heroValueText={`${moneyFull(netDue)} ${netCcy}`}
          heroMeta={`${netCcy} · ${dayFmt.format(at)} · ${timeFmt.format(
            at
          )} UTC · ${byLabel(selectedCall.marked_by, selectedCall.source)}`}
          chips={[
            {
              label: 'Balance after call',
              value: moneyAbbrev(poolAfter),
              // LR-542 (variant A): the pool is a running balance, not an in-our-favour
              // reading — no green/red on its sign.
              tone: 'neutral',
            },
            ...repoFxChips(selectedCall),
          ]}
        />
        <SectionBar title="Contributing repo trades" meta="repo margin call" />
        <RepoCashPoolLedger
          key={selectedCall.id}
          calls={[selectedCall]}
          defaultExpanded
          origin={TRADE_ORIGIN}
        />
      </>
    );
  }
  return (
    <HistoryLayout
      body={body}
      resultCount={searching && !feed.isPlaceholderData ? feed.totalCount : undefined}
      railPaging={feed}
      loadMoreLabel="Load more postings"
      railList={
        <DateGroupedList
          groups={groups}
          selectedId={selectedCall ? String(selectedCall.id) : undefined}
          onSelect={setSel}
          emptyLabel="No matching postings."
        />
      }
      detail={detail}
    />
  );
};

const repoBookFilters = (key: string | undefined): HistoryFeedFilters | undefined =>
  key ? { counterparty_name: key } : undefined;

// By counterparty: one repo book per counterparty, whatever currency each call was posted in
// (calls from before the agreement currency are in their trade currency; a change of the agreement
// currency starts a new one).
const CashPoolByCounterparty: React.FC<BodyProps> = (body) => {
  const { sel, query, setSel } = body;
  const searching = query !== '';
  const books = useRepoMarginCallBooks({ search: query || undefined });
  // A saved link may still carry `counterparty|currency`.
  const selected = useSelectedBook(
    books.items,
    sel?.replace(/\|[A-Z]{3}$/, ''),
    searching,
    repoBookKey,
    repoBookFilters
  );
  const calls = useRepoMarginCallPages(selected.filters ?? {}, {
    enabled: selected.filters !== undefined,
  });
  const head = calls.items[0] ?? selected.listed?.latest;
  const [range, setRange] = useState<ChartRange>('3M');
  const { rejectIfEmpty } = selected;
  const callsSettledEmpty = calls.isSuccess && calls.items.length === 0;
  useEffect(() => rejectIfEmpty(callsSettledEmpty), [rejectIfEmpty, callsSettledEmpty]);
  useRangeCovered(calls.items[calls.items.length - 1]?.called_at, calls, range);

  if (books.isPending) return <HistorySkeleton />;
  if (books.error && !books.data) {
    return (
      <HistoryError message="Failed to load cash-pool postings." onRetry={() => void books.refetch()} />
    );
  }
  if (!searching && !books.isPlaceholderData && books.items.length === 0) {
    return <HistoryEmpty tab="cashpool" />;
  }

  const now = new Date();
  const rows: CpListRow[] = books.items.map((b) => {
    const latest = b.latest;
    // LR-542 (feedback #63): rail balance + sparkline are displayed billing-signed.
    const bal = displayCashPool(Number(latest.cashpool_after));
    const when = new Date(latest.called_at);
    // LR-493: cashpool_after is denominated in the SETTLEMENT currency, so the value tag must be the
    // settlement ccy — tagging it with the trade ccy (latest.currency) mislabels a cross-settle
    // balance (an EUR figure tagged GBP) and contradicts the detail hero.
    const { currency: railCcy, metaPrefix } = poolRailTag(latest.currency, latest.settlement_currency);
    const lastCall = `last call ${relDayWord(when, now)} · ${timeFmt.format(when)} UTC`;
    return {
      key: repoBookKey(b),
      cp: b.counterparty_name,
      currency: railCcy,
      meta: metaPrefix + lastCall,
      value: bal,
      // LR-542: the caption states the interest direction of the displayed balance (the old
      // 'due to us / due to CP' wording described the NET DUE axis, not the pool, and read
      // backwards even under the previous convention).
      valueCaption: cashPoolRailCaption(bal),
      // LR-542 (variant A): a balance is not an in-our-favour reading — neutral figure/spark.
      valueTone: 'neutral',
      spark: b.spark.map((v) => displayCashPool(Number(v))),
    };
  });

  let detail: React.ReactNode;
  if (!head) {
    detail = calls.error ? (
      <HistoryError
        message="Failed to load this counterparty's postings."
        onRetry={() => void calls.refetch()}
      />
    ) : books.items.length === 0 ? (
      <NoMatchDetail noun="counterparties" />
    ) : (
      <DetailSkeleton />
    );
  } else {
    // A running balance only reads as one series in one currency: the chart (like the rail's spark)
    // takes the calls in the newest call's currency; the posting history below lists them all.
    const headCcy = settleCcyOf(head);
    const otherCurrencies = [
      ...new Set(calls.items.map(settleCcyOf).filter((ccy) => ccy !== headCcy)),
    ];
    const chartData: MarginChartDatum[] = [
      ...clampByRange(
        calls.items.filter((c) => settleCcyOf(c) === headCcy),
        (c) => c.called_at,
        range,
        now
      ),
    ]
      .reverse()
      .map((c) => {
        const displayed = displayCashPool(Number(c.cashpool_after));
        return {
          date: dayShortFmt.format(new Date(c.called_at)),
          value: displayed,
          label: signedNumber(displayed),
        };
      });
    detail = (
      <>
        <RepoBookHeader
          head={head}
          chart={{
            title: 'Balance at each call',
            subtitle:
              otherCurrencies.length > 0
                ? `calls in ${headCcy} · earlier ones in ${otherCurrencies.join(', ')} are in the posting history`
                : 'hover a point',
            range,
            onRangeChange: setRange,
            node: (
              <MarginChart
                key={selected.key}
                data={chartData}
                // LR-542 (variant A): neutral/informational accent — the old SIGN_COLOR.pos
                // green implied "in our favour", which the billing-signed balance is not. The
                // tooltip follows: without tipTone the always-on headline would paint the
                // everyday we-hold-CP-cash state (displayed negative) red.
                accent={EXPOSURE_ACCENT}
                variant="line"
                unit="money"
                zeroLine
                tipTone="neutral"
              />
            ),
          }}
        />
        <SectionBar title="Posting history" meta="repo margin calls" />
        {/* As in Mark to Market: the ledger waits for the book's own postings. */}
        {calls.data !== undefined ? (
          <RepoCashPoolLedger key={selected.key} calls={calls.items} origin={TRADE_ORIGIN} />
        ) : (
          calls.error && (
            <HistoryError
              message="Failed to load this counterparty's postings."
              onRetry={() => void calls.refetch()}
            />
          )
        )}
        <LoadMore
          hasMore={calls.hasNextPage}
          isLoading={calls.isFetchingNextPage || (calls.isPending && calls.fetchStatus === 'fetching')}
          isError={calls.isFetchNextPageError}
          onLoadMore={() => void calls.fetchNextPage()}
          label="Load older postings"
        />
      </>
    );
  }
  return (
    <HistoryLayout
      body={body}
      resultCount={searching && !books.isPlaceholderData ? books.totalCount : undefined}
      railPaging={books}
      loadMoreLabel="Load more counterparties"
      railList={
        <CounterpartyList
          groups={groupByName(rows)}
          selectedKey={selected.key}
          onSelect={setSel}
          headerTag={{ label: 'Repo cash pool', tone: 'warn' }}
          // One row per repo book: a count of currencies would always read "1".
          showBookCount={false}
          emptyLabel="No matching counterparties."
        />
      }
      detail={detail}
    />
  );
};

const CashPoolHistory: React.FC<BodyProps> = (body) =>
  body.hview === 'date' ? <CashPoolByDate {...body} /> : <CashPoolByCounterparty {...body} />;

// ─── orchestrator ────────────────────────────────────────────────────────────

export interface HistoryShellProps {
  tab: 'mtm' | 'cashpool';
}

export const HistoryShell: React.FC<HistoryShellProps> = ({ tab }) => {
  const navigate = useNavigate();
  const search = useSearch({ from: '/trades' });
  const hview: HistoryView = search.hview ?? (tab === 'cashpool' ? 'cp' : 'date');
  const sel = search.sel;
  const [query, setQuery] = useState('');

  const setHview = useCallback(
    (v: HistoryView) => {
      // Switching the view changes what "selected" means, so drop the stale selection.
      navigate({ to: '/trades', search: { ...search, hview: v, sel: undefined } });
    },
    [navigate, search]
  );
  const setSel = useCallback(
    (s: string) => {
      navigate({ to: '/trades', search: { ...search, sel: s } });
    },
    [navigate, search]
  );
  const goToMtm = useCallback(() => {
    // LR-587: the marking workspace lives in Collateral Manager now. The product
    // follows the history tab this button sits in — the cash-pool tab lists repo
    // margin calls, so sending it to GMSLA / Cash Pool Fee landed the trader in a
    // grid that cannot contain the postings they were just reading (dp review,
    // Low 9). GMSLA's default leg is Cash Pool Fee; GMRA has the one leg.
    navigate({
      to: '/collateral',
      search:
        tab === 'cashpool'
          ? { product: 'gmra' as const, leg: 'cash' as const, cp: undefined }
          : { product: 'gmsla' as const, leg: 'fee' as const, cp: undefined },
    });
  }, [navigate, tab]);

  const body: BodyProps = { hview, sel, query, setQuery, setHview, setSel, goToMtm };

  return tab === 'mtm' ? <MtmHistory {...body} /> : <CashPoolHistory {...body} />;
};
